/** @format */

import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { assetUrl } from "fusion-core";
import QuoteStep from "../enums/QuoteStep";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import TqAddDriverMapper from "../mappers/tqAddDriverMapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import TqAddDriverModal from "../components/tqAddDriverModal";
import useQuoteStore from "../store/QuoteStore";
import { saveQuote, fetchPanelData } from "../services/quoteService";
import { fetchPolicyDetails } from "../services/quoteService";
import {
  deformatDateT,
  deformatMonthT,
  deformatYearT,
} from "../../../shared/utils/formatters";
import { handleNext } from "../../../shared/utils/navigationUtils";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { useNavigate } from "fusion-plugin-react-router";
import { downloadTOBA } from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";

const TqAddDriver = () => {
  const STATE = useAppStateStore();
  const claims = STATE.claims;
  const additionalDriver = STATE.additionalDriver;
  const setAdditionalDriver = STATE.setAdditionalDriver;

  const methods = useForm();
  const { setValue, register, watch } = methods;
  const navigate = useNavigate();

  const [drivers, setDrivers] = useState([]);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [localClaimFields, setLocalClaimFields] = useState([]);

  const maxDriversAllowed = 4;

  const car = assetUrl("../../../assets/icons/Car.svg");
  const trash = assetUrl("../../../assets/icons/trash-filled.svg");
  const pencil = assetUrl("../../../assets/icons/Group22.svg");

  const fetchDrivers = () => {
    const storeDrivers = TqAddDriverMapper.get();
    setDrivers(storeDrivers);
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleDelete = (index) => {
    TqAddDriverMapper.delete(index);
    fetchDrivers();
  };

  const handleDriverEdit = (driver, index) => {
    setDriverModalOpen(true);
    setValue("driverIndex", index);

    // Set form values based on driver data
    setValue("addDriverTitle", {
      value: driver.titleId,
      label: driver.titleDescription,
    });
    setValue("addDriver_firstName", driver.forename);
    setValue("addDriver_lastName", driver.surname);
    setValue("addDob_Date", deformatDateT(driver.dateOfBirth));
    setValue("addlicense", driver.licenceNumber?.slice(0, 11));
    setValue("addlicenseUser", driver.licenceNumber?.slice(11));
    setValue("rating", {
      value: driver.uberDriverRatingId,
      label: driver.uberDriverRatingDescription,
    });
    setValue("relation", {
      value: driver.relationshipId,
      label: driver.relationshipDescription,
    });
    setValue("martialStatus", {
      value: driver.maritalStatusId,
      label: driver.maritalStatusDescription,
    });
    setValue(
      "AddDriverbatch_Month.value",
      deformatMonthT(driver.qualifications[0].datePassed)
    );
    setValue(
      "AddDriverbatch_Year.value",
      deformatYearT(driver.qualifications[0].datePassed)
    );
    setValue("residecy", driver.residentSinceBirth ? "Yes" : "No");
    setValue("residencyYears", driver.ukResidenceYears);
    setValue("addvehicleUse", driver.vehicleUseId);

    // Set occupation details
    if (driver.occupations?.[0]) {
      setValue("occupation", {
        value: driver.occupations[0].occupationId,
      });
      setValue("addDriverempStatus", {
        value: driver.occupations[0].employmentStatusId,
      });
      setValue("addDriverBusinessOccupation", {
        value: driver.occupations[0].employersBusinessId,
      });
    }
    if (driver.occupations?.[1]) {
      setValue("parttime", "Yes");
      setValue("addDriverpartTimeOcc", {
        value: driver.occupations[1].occupationId,
      });
      setValue("addDriverPartTimeEmpStatus", {
        value: driver.occupations[1].employmentStatusId,
      });
      setValue("addDriverpartTimeBusinessOcc", {
        value: driver.occupations[1].employersBusinessId,
      });
    } else {
      setValue("parttime", "No");
    }
    // Set claims if they exist
    if (driver.claims && driver.claims.length > 0) {
      setValue("AdditionalClaim", "Yes");
      const formattedClaims = driver.claims.map((claim) => ({
        id: claim.claimDetailsId,
        typeId: claim.typeId,
        claimDate: claim.claimDate,
        cost: claim.cost,
        injuryInvolved: claim.injuryInvolved,
        atFault: claim.atFault,
        objectId: claim.objectId || null,
        claimDetailsId: claim.claimDetailsId || null,
        theftFromHome: claim.theftFromHome || false,
        statusId: claim.statusId || null,
        ncdAffected: claim.ncdAffected || false,
        incurredOnPolicy: claim.incurredOnPolicy || false,
        referenceNumber: claim.referenceNumber || null,
        parentObjectId: claim.parentObjectId || null,
        parentObjectHistoryId: claim.parentObjectHistoryId || 0,
      }));

      setLocalClaimFields(formattedClaims);
    } else {
      setValue("AdditionalClaim", "No");
      setLocalClaimFields([]);
    }
  };

  const handleAddDriver = () => {
    methods.reset({
      driverIndex: null,
      addDriverTitle: null,
      addDriver_firstName: "",
      addDriver_lastName: "",
      addDob_Date: null,
      addlicense: "",
      addlicenseUser: "",
      rating: null,
      relation: null,
      occupation: null,
      addDriverempStatus: null,
      addDriverBusinessOccupation: null,
      addDriverpartTimeOcc: null,
      addDriverPartTimeEmpStatus: null,
      addDriverpartTimeBusinessOcc: null,
      parttime: null,
      residecy: null,
      residencyYears: null,
      martialStatus: null,
      AddDriverbatch_Month: null,
      AddDriverbatch_Year: null,
      addvehicleUse: null,
      AdditionalClaim: null,
      additionalDriverClaims: [],
    });

    if (drivers.length < maxDriversAllowed) {
      setDriverModalOpen(true);
    }
  };

  useEffect(() => {
    if (driverModalOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [driverModalOpen]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQADDDRIVER_ENTERED);
  }, []);

  const [onlyDriver, setOnlyDriver] = useState(additionalDriver);

  useEffect(() => {
    setOnlyDriver(additionalDriver);
    // eslint-disable-next-line
  }, [additionalDriver]);

  const onClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQADDDRIVER_NEXT_CLICKED);
    setAdditionalDriver(onlyDriver);
    const quoteData = useQuoteStore.getState().quote;
    saveQuote(quoteData, navigate).then((data) => {
      fetchPolicyDetails().then(() => {
        fetchPanelData(null, data.risk.riskObjectId).then(() => {
          handleNext(QuoteStep.TQ_PANEL);
        });
      });
    });
    handleNext(QuoteStep.TQ_PANEL_LOADER);
  };

  return (
    <FormProvider {...methods}>
      <QuoteFormWrapper>
        <h1>Additional Drivers</h1>
        <p>
          It is your duty to make a fair representation of the risk. This means
          you must describe your business accurately and disclose every material
          fact or circumstance known to you or to anyone who is responsible for
          your insurance. Please see our{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              downloadTOBA({ navigate });
            }}
            className="transparentButton"
          >
            Terms of Business{" "}
          </a>
          for further information. Please ensure you have permission from the
          driver to use their information.
        </p>

        <label className="labelName">
          Do you need to add an additional driver who needs to be included on
          the policy?
        </label>
        <div className="fields">
          <div className="driverFields">
            {drivers.length > 0 &&
              drivers.map((driver, index) => (
                <div className="addDriverDiv" key={index}>
                  <div className="divimgname">
                    <img className="imgDesign" src={car} alt="" />
                    <h2 className="divname">
                      {driver.forename} {driver.surname}
                    </h2>
                  </div>
                  <div className="btnimagestyle">
                    <button
                      type="button"
                      onClick={() => handleDriverEdit(driver, index)}
                      className="imgDim btnpencil"
                    >
                      <img className="imgDesign1" src={pencil} alt="" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(index)}
                      className="imgDim"
                    >
                      <img className="imgDesign2" src={trash} alt="" />
                    </button>
                  </div>
                </div>
              ))}
            {drivers.length < maxDriversAllowed && (
              <div className="vertical-line">
                <div className="addDrivers">
                  <button
                    type="button"
                    className="lookupBtn bg-gray"
                    onClick={handleAddDriver}
                    disabled={onlyDriver === "No"}
                  >
                    <strong>Add additional Driver</strong>
                  </button>
                </div>
              </div>
            )}

            {drivers.length === maxDriversAllowed && (
              <p className="errorField claim-error">
                Your Additional Driver Limit is Reached
              </p>
            )}
          </div>
          {drivers.length < 1 && (
            <>
              <p>
                If you no longer want to add an additional driver. Please select
                yes to the question below
              </p>
              <label className="labelName">
                Are you the only driver insured on the vehicle?
              </label>
              <div className="radioGroup">
                <label className="radioLabel">
                  <p className="radioLabels">Yes</p>
                  <input
                    type="radio"
                    name="onlyDriver"
                    value="No"
                    checked={onlyDriver === "No"}
                    onChange={() => setOnlyDriver("No")}
                  />
                  <span className="checkmark"></span>
                </label>
                <label className="radioLabel">
                  <p className="radioLabels">No</p>
                  <input
                    type="radio"
                    name="onlyDriver"
                    value="Yes"
                    checked={onlyDriver === "Yes"}
                    onChange={() => setOnlyDriver("Yes")}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>
            </>
          )}
        </div>
      </QuoteFormWrapper>
      <FormNavigationButtons
        type="button"
        prevStep={claims === "Yes" ? QuoteStep.TQ_CLAIM : QuoteStep.TQ6}
        onClick={onClick}
        isValid={drivers.length > 0 || onlyDriver === "No"}
      />

      <TqAddDriverModal
        onDriverSaved={fetchDrivers}
        isModalOpen={driverModalOpen}
        setIsModalOpen={setDriverModalOpen}
        localClaimFields={localClaimFields}
        setLocalClaimFields={setLocalClaimFields}
      />
    </FormProvider>
  );
};

export default TqAddDriver;
